"use client";

import React, { useEffect, useState, useRef } from "react";
import {
  useForm,
  SubmitHandler,
  FieldValues,
  UseFormReturn,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form } from "@/components/ui/form";
import { cn } from "@/lib/utils";

// Generic form field configuration
type FormFieldConfig<T extends FieldValues> = {
  name: keyof T;
  label: string;
  type:
    | "text"
    | "email"
    | "password"
    | "number"
    | "textarea"
    | "select"
    | "checkbox"
    | "radio"
    | "date";
  placeholder?: string;
  options?: Array<{ value: string; label: string }>; // For select, radio
  validation?: z.ZodTypeAny;
  className?: string;
  disabled?: boolean;
  required?: boolean;
};

// Generic form props
type CustomFormWithValidationsProps<T extends FieldValues> = {
  fields?: FormFieldConfig<T>[];
  onSubmit: SubmitHandler<T>;
  schema: z.ZodSchema<T>;
  defaultValues?: Partial<T>;
  hydrateData?: () => Promise<Partial<T>>;
  className?: string;
  children?: (form: UseFormReturn<T>) => React.ReactNode;
  onDataLoading?: (loading: boolean) => void;
  onDataError?: (error: Error) => void;
};

export default function CustomFormWithValidations<T extends FieldValues>({
  fields,
  onSubmit,
  schema,
  defaultValues,
  hydrateData,
  className,
  children,
  onDataLoading,
  onDataError,
}: CustomFormWithValidationsProps<T>) {
  const [hasHydrated, hasHydratedSet] = useState(false);
  const isLoadingRef = useRef(false);

  const form = useForm({
    resolver: zodResolver(schema as never) as never,
    defaultValues: (defaultValues || {}) as never,
  }) as unknown as UseFormReturn<T>;

  // Effect to handle async data hydration
  useEffect(() => {
    if (hasHydrated || isLoadingRef.current || !hydrateData) return;

    const loadData = async () => {
      isLoadingRef.current = true;

      try {
        onDataLoading?.(true);

        const data = await hydrateData();

        // Reset form with new values
        form.reset(data as never);
        hasHydratedSet(true);
      } catch (error) {
        console.error("Data hydration error:", error);
        onDataError?.(error as Error);
        hasHydratedSet(true);
      } finally {
        onDataLoading?.(false);
        isLoadingRef.current = false;
      }
    };

    loadData();
  }, [hydrateData, hasHydrated, form, onDataLoading, onDataError]);

  const handleSubmit = async (data: unknown) => {
    try {
      await onSubmit(data as T);
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className={cn("space-y-6", className)}
      >
        {/* Render configured fields */}
        {fields?.map((field) => (
          <FormFieldRenderer
            key={String(field.name)}
            field={field}
            form={form}
          />
        ))}

        {/* Render custom children if provided */}
        {children && children(form)}
      </form>
    </Form>
  );
}

// Field renderer component
function FormFieldRenderer<T extends FieldValues>({
  field,
  form,
}: {
  field: FormFieldConfig<T>;
  form: UseFormReturn<T>;
}) {
  const {
    name,
    label,
    type,
    placeholder,
    options,
    className,
    disabled,
    required,
  } = field;

  return (
    <div className={cn("space-y-2", className)}>
      <label htmlFor={String(name)} className="text-sm font-medium">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {type === "text" ||
      type === "email" ||
      type === "password" ||
      type === "number" ? (
        <input
          {...form.register(name as never)}
          id={String(name)}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        />
      ) : type === "textarea" ? (
        <textarea
          {...form.register(name as never)}
          id={String(name)}
          placeholder={placeholder}
          disabled={disabled}
          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        />
      ) : type === "select" ? (
        <select
          {...form.register(name as never)}
          id={String(name)}
          disabled={disabled}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <option value="">{placeholder || "Select an option"}</option>
          {options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : type === "checkbox" ? (
        <div className="flex items-center space-x-2">
          <input
            {...form.register(name as never)}
            id={String(name)}
            type="checkbox"
            disabled={disabled}
            className="h-4 w-4 rounded border border-input"
          />
          <label htmlFor={String(name)} className="text-sm">
            {placeholder || label}
          </label>
        </div>
      ) : type === "date" ? (
        <input
          {...form.register(name as never)}
          id={String(name)}
          type="date"
          disabled={disabled}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        />
      ) : null}

      {form.formState.errors[name] && (
        <p className="text-sm text-red-500">
          {form.formState.errors[name]?.message as string}
        </p>
      )}
    </div>
  );
}
